import { useGameContainer } from '../index';
import ContentSection, { ContentSectionHeader } from '@/components/ContentSection';
import { SectionHeaderSize } from '@/components/SectionHeader';
import styles from './GamePrediction.module.scss';
import TeamImage from 'components/TeamImage';

const SBLIVE_BLUE_FALLBACK = '#3e89f7'; // Corresponds to $sbl-blue-1
const NEUTRAL_GRAY_FALLBACK = '#5b667c'; // Corresponds to $sbl-blue-gray-2
const COLOR_SIMILARITY_THRESHOLD = 150;

interface RGBColor {
  r: number;
  g: number;
  b: number;
}

function hexToRgb(hex: string | undefined | null): RGBColor | null {
  if (!hex) return null;
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : null;
}

function colorDistance(rgb1: RGBColor, rgb2: RGBColor): number {
  return Math.sqrt(Math.pow(rgb1.r - rgb2.r, 2) + Math.pow(rgb1.g - rgb2.g, 2) + Math.pow(rgb1.b - rgb2.b, 2));
}

function areColorsTooSimilar(color1Hex: string | undefined | null, color2Hex: string | undefined | null, threshold: number): boolean {
  if (!color1Hex || !color2Hex) return false; // If one is missing, they aren't "similar team colors"
  const rgb1 = hexToRgb(color1Hex);
  const rgb2 = hexToRgb(color2Hex);
  if (!rgb1 || !rgb2) return false; // Invalid hex, treat as not similar for this check
  return colorDistance(rgb1, rgb2) < threshold;
}

interface PieSegmentProps {
  startAngle: number;
  endAngle: number;
  color: string;
  center: number;
  radius: number;
  strokeColor?: string;
}

const PieSegment: React.FC<PieSegmentProps> = ({ startAngle, endAngle, color, center, radius, strokeColor }) => {
  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians)
    };
  };

  const createPath = (startAngle: number, endAngle: number): string => {
    const start = polarToCartesian(center, center, radius, endAngle);
    const end = polarToCartesian(center, center, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

    return `M ${center} ${center} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y} Z`;
  };

  return (
    <path
      d={createPath(startAngle, endAngle)}
      fill={color}
      stroke={strokeColor}
      strokeWidth={strokeColor ? 2 : 0}
      className={styles.PieSegment}
    />
  );
};

export default function GamePrediction() {
  const [{ game }] = useGameContainer();

  if (!game?.contest?.contestParticipants || game.contest.contestParticipants.length < 2) {
    return null;
  }

  const [participant1, participant2] = game.contest.contestParticipants;

  if (
    participant1?.winChance === null ||
    participant1?.winChance === undefined ||
    participant2?.winChance === null ||
    participant2?.winChance === undefined
  ) {
    return null;
  }

  const team1WinChance = participant1.winChance;
  const team2WinChance = participant2.winChance;

  const gameTeam1 = game.gameTeams?.find(gt => gt.team.id === (participant1.participant as { id: string })?.id)?.team;
  const gameTeam2 = game.gameTeams?.find(gt => gt.team.id === (participant2.participant as { id: string })?.id)?.team;

  if (!gameTeam1 || !gameTeam2) {
    return null;
  }

  const formatPercentage = (percentage: number) => {
    return percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(1);
  };

  const size = 160;
  const center = size / 2;
  const radius = 70;
  const team1Angle = (team1WinChance / 100) * 360;

  let team1FillColor: string;
  let team1StrokeColor: string | undefined;
  let team2FillColor: string;
  let team2StrokeColor: string | undefined;

  const p1Primary = gameTeam1.primaryColor;
  const p1Secondary = gameTeam1.secondaryColor;
  const p2Primary = gameTeam2.primaryColor;
  const p2Secondary = gameTeam2.secondaryColor;

  if (p1Primary && p2Primary && areColorsTooSimilar(p1Primary, p2Primary, COLOR_SIMILARITY_THRESHOLD)) {
    team1FillColor = SBLIVE_BLUE_FALLBACK;
    team1StrokeColor = undefined;
    team2FillColor = NEUTRAL_GRAY_FALLBACK;
    team2StrokeColor = undefined;
  } else {
    team1FillColor = p1Primary || SBLIVE_BLUE_FALLBACK;
    team1StrokeColor = p1Primary ? p1Secondary : undefined;

    team2FillColor = p2Primary || NEUTRAL_GRAY_FALLBACK;
    team2StrokeColor = p2Primary ? p2Secondary : undefined;
  }

  return (
    <ContentSection>
      <ContentSectionHeader size={SectionHeaderSize.LARGE}>SBLive Prediction</ContentSectionHeader>
      <div className={styles.PredictionContent}>
        <div className={styles.ChartAndTeams}>
          <div className={styles.ChartWrapper}>
            <svg className={styles.PieChart} viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
              <PieSegment
                startAngle={0}
                endAngle={team1Angle}
                color={team1FillColor}
                center={center}
                radius={radius}
                strokeColor={team1StrokeColor}
              />
              <PieSegment
                startAngle={team1Angle}
                endAngle={360}
                color={team2FillColor}
                center={center}
                radius={radius}
                strokeColor={team2StrokeColor}
              />
            </svg>
            <div className={styles.CenterContent}>
              <div className={styles.VersusText}>vs</div>
            </div>
          </div>
          <div className={styles.TeamPredictions}>
            <div className={styles.TeamPrediction}>
              <div className={styles.TeamInfo}>
                <TeamImage src={gameTeam1.image} alt={gameTeam1.name} className={styles.TeamLogo} />
                <span className={styles.TeamName}>{gameTeam1.name}</span>
              </div>
              <div className={styles.TeamPercentage}>{formatPercentage(team1WinChance)}%</div>
            </div>
            <div className={styles.TeamPrediction}>
              <div className={styles.TeamInfo}>
                <TeamImage src={gameTeam2.image} alt={gameTeam2.name} className={styles.TeamLogo} />
                <span className={styles.TeamName}>{gameTeam2.name}</span>
              </div>
              <div className={styles.TeamPercentage}>{formatPercentage(team2WinChance)}%</div>
            </div>
          </div>
        </div>
      </div>
    </ContentSection>
  );
}
